import { IColumnConfig, IColumnEditorSettings } from '@/components/Instruments/Interfaces/InstrumentsInterfaces';

const STORAGE_KEY = 'columnEditorSettings';

export const DEFAULT_COLUMNS: IColumnConfig[] =
[
    { key: 'nameIsin', label: 'Nombre/ISIN', enabled: true, locked: true },
    { key: 'currency', label: 'Divisa', enabled: true },
    { key: 'ongoingCharges', label: 'Gastos Corrientes (OGC)', enabled: true },
    { key: 'rebate', label: 'Retrocesión', enabled: true },
    { key: 'minimumInitialInvestment', label: 'Inversión Inicial Mínima', enabled: true },
    { key: 'income', label: 'Dividendos', enabled: true },
    { key: 'noMin', label: 'Aplica Mínimo', enabled: true },
    { key: 'cleanShare', label: 'Clase Limpia', enabled: true },
    { key: 'available', label: 'Disponible', enabled: true },
];

export const DEFAULT_RESULTS_PER_PAGE = 10;

/**
 * Reconcilia las columnas guardadas en localStorage con las columnas
 * definidas en el código (DEFAULT_COLUMNS).
 *
 * - Columnas que ya no existen en DEFAULT_COLUMNS se eliminan.
 * - Columnas nuevas en DEFAULT_COLUMNS se añaden al final con su estado por defecto.
 * - Columnas locked se fuerzan a enabled: true.
 * - Se mantiene el orden guardado para las columnas existentes.
 */
const reconcileColumns = (stored: Array<{key: string; enabled: boolean}>): IColumnConfig[] =>
{
    const defaultMap = new Map<string, IColumnConfig>();
    for(const col of DEFAULT_COLUMNS)
    {
        defaultMap.set(col.key, col);
    }

    const reconciledKeys = new Set<string>();
    const reconciled: IColumnConfig[] = [];

    // Mantener el orden de las columnas guardadas, eliminando las que ya no existen
    for(const storedCol of stored)
    {
        const defaultCol = defaultMap.get(storedCol.key);
        if(defaultCol)
        {
            reconciled.push(
            {
                ...defaultCol,
                enabled: defaultCol.locked ? true : storedCol.enabled,
            });
            reconciledKeys.add(storedCol.key);
        }
    }

    // Añadir columnas nuevas que no existían en localStorage
    for(const defaultCol of DEFAULT_COLUMNS)
    {
        if(!reconciledKeys.has(defaultCol.key))
        {
            reconciled.push({ ...defaultCol });
        }
    }

    return reconciled;
};

export const loadColumnEditorSettings = (): IColumnEditorSettings =>
{
    const stored = localStorage.getItem(STORAGE_KEY);

    if(stored)
    {
        const parsed = JSON.parse(stored);

        const storedColumns: Array<{ key: string; enabled: boolean }> = parsed.columns || [];
        const columns = reconcileColumns(storedColumns);

        const resultsPerPage = typeof parsed.resultsPerPage === 'number'
            ? parsed.resultsPerPage
            : DEFAULT_RESULTS_PER_PAGE;

        return { resultsPerPage, columns };
    }

    return {
        resultsPerPage: DEFAULT_RESULTS_PER_PAGE,
        columns: DEFAULT_COLUMNS.map(col => ({ ...col })),
    };
};

export const saveColumnEditorSettings = (settings: IColumnEditorSettings): void =>
{
    const toStore =
    {
        resultsPerPage: settings.resultsPerPage,
        columns: settings.columns.map(col => ({ key: col.key, enabled: col.enabled })),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
};